// src/lib/dashboard/eficienciaEquipes.ts
//
// BI de eficiência das equipes de Goiânia e Morrinhos (#6198, pedido do Igor).
//
// Os números brutos vêm da RPC `bi_eficiencia_equipes` (uma linha por pessoa e
// indicador) e, para o prazo do Faturamento, das emissões de boleto — com as
// mesmas regras do Dashboard de Faturamento (`metricasFaturamento.ts`). Este
// arquivo junta os dois, decide quais colunas cada equipe mostra e calcula a
// linha da equipe. Tela e planilha usam as mesmas funções: o Excel nunca diz
// uma coisa e a tela outra.
//
// Princípio: medir ENTREGA, não clique. Por isso quase tudo conta itens
// diferentes (fatura, cliente, negociação), e "dias ativos" aparece só como
// presença — nunca como produção.

import type { EmissaoFaturamento } from '../financeiro/metricasFaturamento.ts';
import { contaComoFaturamento, emissaoForaDoPrazo, tempoDeFaturamentoEmDias } from '../financeiro/metricasFaturamento.ts';
import { nomeDepartamento } from '../departamentos.ts';

export type Unidade = 'Goiânia' | 'Morrinhos';
export const UNIDADES: Unidade[] = ['Goiânia', 'Morrinhos'];

export const ehUnidade = (v: unknown): v is Unidade => v === 'Goiânia' || v === 'Morrinhos';

/** Uma linha devolvida pela RPC. `valor` chega como número ou texto (numeric). */
export interface LinhaIndicador {
  user_id: string;
  indicador: string;
  valor: number | string;
}

/** Valores brutos de uma pessoa, por chave. Chave ausente = zero (a RPC omite zeros). */
export type Valores = Record<string, number>;

export interface PessoaBi {
  id: string;
  nome: string;
  departamento: string | null;
  unidade: Unidade;
}

export type FormatoIndicador = 'num' | 'pct' | 'brl' | 'kwh' | 'dias' | 'horas';

export type CalculoIndicador =
  /** Valor direto da RPC. `equipe` diz como a linha da equipe agrega. */
  | { tipo: 'bruto'; chave: string; equipe: 'soma' | 'media' }
  /** Razão entre dois brutos. Na equipe é soma ÷ soma — nunca média de médias. */
  | { tipo: 'razao'; numerador: string; denominador: string };

export interface Indicador {
  id: string;
  rotulo: string;
  /** O que é medido e de onde vem — aparece no ícone de ajuda da coluna. */
  explicacao: string;
  formato: FormatoIndicador;
  calculo: CalculoIndicador;
}

const bruto = (chave: string, equipe: 'soma' | 'media' = 'soma'): CalculoIndicador => ({ tipo: 'bruto', chave, equipe });
const razao = (numerador: string, denominador: string): CalculoIndicador => ({ tipo: 'razao', numerador, denominador });

export const INDICADORES: Record<string, Indicador> = {
  dias_ativos: {
    id: 'dias_ativos', rotulo: 'Dias ativos', formato: 'num', calculo: bruto('dias_ativos', 'media'),
    explicacao: 'Dias em que a pessoa abriu o sistema, em qualquer tela. Mostra presença, não produção. Na linha da equipe, é a média por pessoa.',
  },

  // ── Faturamento ──
  faturas_calculadas: {
    id: 'faturas_calculadas', rotulo: 'Faturas calculadas', formato: 'num', calculo: bruto('faturas_calculadas'),
    explicacao: 'Faturas diferentes que a pessoa calculou. Recalcular a mesma fatura não conta de novo.',
  },
  calculadas_por_dia: {
    id: 'calculadas_por_dia', rotulo: 'Faturas por dia', formato: 'num', calculo: razao('faturas_calculadas', 'dias_com_calculo'),
    explicacao: 'Média de faturas calculadas nos dias em que a pessoa calculou alguma. Dia sem cálculo (folga, outra tarefa) não puxa a média para baixo.',
  },
  faturas_validadas: {
    id: 'faturas_validadas', rotulo: 'Faturas validadas', formato: 'num', calculo: bruto('faturas_validadas'),
    explicacao: 'Faturas diferentes em que a pessoa conferiu os saldos e validou.',
  },
  boletos_emitidos: {
    id: 'boletos_emitidos', rotulo: 'Boletos emitidos', formato: 'num', calculo: bruto('boletos_emitidos'),
    explicacao: 'Faturas diferentes em que a pessoa emitiu o boleto.',
  },
  faturas_puladas: {
    id: 'faturas_puladas', rotulo: 'Faturas puladas', formato: 'num', calculo: bruto('faturas_puladas'),
    explicacao: 'Faturas que a pessoa marcou para não calcular (sem injeção, desconto de 100% e outros motivos).',
  },
  pct_corrigidas: {
    id: 'pct_corrigidas', rotulo: 'Voltaram p/ correção', formato: 'pct', calculo: razao('faturas_corrigidas', 'faturas_calculadas'),
    explicacao: 'Das faturas que a pessoa calculou, quantas depois voltaram de etapa ou foram excluídas (por qualquer pessoa). Quanto menor, melhor.',
  },
  pct_fora_prazo: {
    id: 'pct_fora_prazo', rotulo: 'Fora do prazo', formato: 'pct', calculo: razao('emissoes_fora_prazo', 'emissoes'),
    explicacao: 'Boletos emitidos depois do prazo padrão (5 dias úteis antes do vencimento da Equatorial). Só a 1ª emissão de cada fatura; mesma regra do Dashboard de Faturamento.',
  },
  tempo_emissao: {
    id: 'tempo_emissao', rotulo: 'Tempo até o boleto', formato: 'dias', calculo: razao('emissoes_dias_soma', 'emissoes_com_tempo'),
    explicacao: 'Média de dias entre a fatura chegar ao sistema e o boleto ser emitido.',
  },

  // ── Financeiro / cobrança ──
  boletos_enviados: {
    id: 'boletos_enviados', rotulo: 'Boletos enviados', formato: 'num', calculo: bruto('boletos_enviados'),
    explicacao: 'Faturas diferentes que a pessoa enviou ao cliente.',
  },
  cobrancas_email: {
    id: 'cobrancas_email', rotulo: 'Cobranças por e-mail', formato: 'num', calculo: bruto('cobrancas_email'),
    explicacao: 'Faturas diferentes com cobrança enviada por e-mail pela pessoa.',
  },
  clientes_assumidos: {
    id: 'clientes_assumidos', rotulo: 'Clientes assumidos', formato: 'num', calculo: bruto('clientes_assumidos'),
    explicacao: 'Clientes inadimplentes que a pessoa assumiu na carteira de cobrança.',
  },
  contatos_cobranca: {
    id: 'contatos_cobranca', rotulo: 'Contatos de cobrança', formato: 'num', calculo: bruto('contatos_cobranca'),
    explicacao: 'Contatos com inadimplentes registrados no histórico de cobrança (ligação, WhatsApp, presencial).',
  },
  promessas_pagamento: {
    id: 'promessas_pagamento', rotulo: 'Promessas de pagamento', formato: 'num', calculo: bruto('promessas_pagamento'),
    explicacao: 'Contatos de cobrança que terminaram com o cliente prometendo pagar.',
  },
  acordos_criados: {
    id: 'acordos_criados', rotulo: 'Acordos criados', formato: 'num', calculo: bruto('acordos_criados'),
    explicacao: 'Parcelamentos que a pessoa montou no período.',
  },
  acordos_efetivados: {
    id: 'acordos_efetivados', rotulo: 'Acordos efetivados', formato: 'num', calculo: bruto('acordos_efetivados'),
    explicacao: 'Parcelamentos montados pela pessoa que foram efetivados no período.',
  },
  clientes_recuperados: {
    id: 'clientes_recuperados', rotulo: 'Clientes que pagaram', formato: 'num', calculo: bruto('clientes_recuperados'),
    explicacao: 'Clientes da carteira de cobrança da pessoa que pagaram no período.',
  },
  valor_recuperado: {
    id: 'valor_recuperado', rotulo: 'Valor recuperado', formato: 'brl', calculo: bruto('valor_recuperado'),
    explicacao: 'Soma do que os clientes da carteira de cobrança da pessoa pagaram no período.',
  },
  pagamentos_confirmados: {
    id: 'pagamentos_confirmados', rotulo: 'Baixas manuais', formato: 'num', calculo: bruto('pagamentos_confirmados'),
    explicacao: 'Pagamentos confirmados à mão pela pessoa (com comprovante).',
  },
  notificacoes_mora: {
    id: 'notificacoes_mora', rotulo: 'Notificações de mora', formato: 'num', calculo: bruto('notificacoes_mora'),
    explicacao: 'Notificações de mora geradas pela pessoa.',
  },
  termos_cancelamento: {
    id: 'termos_cancelamento', rotulo: 'Termos de cancelamento', formato: 'num', calculo: bruto('termos_cancelamento'),
    explicacao: 'Termos de cancelamento gerados pela pessoa.',
  },
  analises_finalizadas: {
    id: 'analises_finalizadas', rotulo: 'Análises cadastrais', formato: 'num', calculo: bruto('analises_finalizadas'),
    explicacao: 'Análises cadastrais concluídas pela pessoa, com decisão final.',
  },

  // ── Comercial ──
  negociacoes_novas: {
    id: 'negociacoes_novas', rotulo: 'Negociações novas', formato: 'num', calculo: bruto('negociacoes_novas'),
    explicacao: 'Negociações criadas no período com a pessoa como responsável. Para Closers e BDRs, são os leads que chegam pelo rodízio — volume recebido, não trabalho feito. As importadas do sistema antigo ficam de fora.',
  },
  negociacoes_movimentadas: {
    id: 'negociacoes_movimentadas', rotulo: 'Negociações trabalhadas', formato: 'num', calculo: bruto('negociacoes_movimentadas'),
    explicacao: 'Negociações diferentes que a pessoa mudou de etapa. Marcar como perdida não conta aqui.',
  },
  contratos_assinados: {
    id: 'contratos_assinados', rotulo: 'Contratos assinados', formato: 'num', calculo: bruto('contratos_assinados'),
    explicacao: 'Termos de adesão assinados no período, nas negociações em que a pessoa é responsável.',
  },
  conversao: {
    id: 'conversao', rotulo: 'Conversão', formato: 'pct', calculo: razao('contratos_assinados', 'negociacoes_novas'),
    explicacao: 'Contratos assinados ÷ negociações novas do mesmo período. É aproximado: o contrato assinado agora pode ter nascido em outro mês.',
  },
  kwh_assinado: {
    id: 'kwh_assinado', rotulo: 'kWh assinado', formato: 'kwh', calculo: bruto('kwh_assinado'),
    explicacao: 'Consumo médio mensal somado dos contratos assinados no período.',
  },
  negociacoes_perdidas: {
    id: 'negociacoes_perdidas', rotulo: 'Marcadas como perdidas', formato: 'num', calculo: bruto('negociacoes_perdidas'),
    explicacao: 'Negociações diferentes que a pessoa marcou como perdidas. Limpeza de base em lote aparece aqui.',
  },
  tarefas_concluidas: {
    id: 'tarefas_concluidas', rotulo: 'Tarefas concluídas', formato: 'num', calculo: bruto('tarefas_concluidas'),
    explicacao: 'Tarefas do CRM (visitas, retornos, follow-ups) concluídas pela pessoa.',
  },

  // ── Central de Solicitações (Tecnologia e Marketing) ──
  solicitacoes_concluidas: {
    id: 'solicitacoes_concluidas', rotulo: 'Solicitações concluídas', formato: 'num', calculo: bruto('solicitacoes_concluidas'),
    explicacao: 'Solicitações da Central concluídas com a pessoa como responsável, subtarefas incluídas.',
  },
  pct_no_prazo: {
    id: 'pct_no_prazo', rotulo: 'No prazo', formato: 'pct', calculo: razao('solicitacoes_no_prazo', 'solicitacoes_com_prazo'),
    explicacao: 'Das solicitações concluídas que tinham prazo, quantas terminaram até a data. As sem prazo ficam fora da conta.',
  },
  tempo_entrega: {
    id: 'tempo_entrega', rotulo: 'Tempo de entrega', formato: 'dias', calculo: razao('solicitacoes_dias_soma', 'solicitacoes_concluidas'),
    explicacao: 'Média de dias entre a abertura da solicitação e a conclusão.',
  },
  solicitacoes_atrasadas: {
    id: 'solicitacoes_atrasadas', rotulo: 'Atrasadas agora', formato: 'num', calculo: bruto('solicitacoes_atrasadas'),
    explicacao: 'Solicitações abertas que já passaram do prazo, hoje. Não depende do período escolhido.',
  },
  horas_cronometro: {
    id: 'horas_cronometro', rotulo: 'Horas no cronômetro', formato: 'horas', calculo: bruto('horas_cronometro'),
    explicacao: 'Horas registradas no cronômetro das solicitações.',
  },
};

interface ConjuntoColunas {
  /** Sempre aparecem, mesmo zeradas — são o retrato da função. */
  fixas: string[];
  /** Só aparecem quando alguém da equipe tem valor. */
  extras: string[];
}

const COBRANCA: ConjuntoColunas = {
  fixas: ['dias_ativos', 'boletos_enviados', 'contatos_cobranca', 'acordos_criados', 'clientes_recuperados', 'valor_recuperado'],
  extras: ['cobrancas_email', 'clientes_assumidos', 'promessas_pagamento', 'acordos_efetivados', 'pagamentos_confirmados',
    'notificacoes_mora', 'termos_cancelamento', 'analises_finalizadas', 'faturas_calculadas', 'boletos_emitidos'],
};

const COMERCIAL: ConjuntoColunas = {
  fixas: ['dias_ativos', 'negociacoes_novas', 'negociacoes_movimentadas', 'contratos_assinados', 'conversao', 'kwh_assinado'],
  extras: ['negociacoes_perdidas', 'tarefas_concluidas'],
};

const CENTRAL: ConjuntoColunas = {
  fixas: ['dias_ativos', 'solicitacoes_concluidas', 'pct_no_prazo', 'tempo_entrega', 'solicitacoes_atrasadas', 'horas_cronometro'],
  extras: [],
};

/** Colunas por departamento. Departamento fora desta lista cai em `PADRAO`. */
const COLUNAS_POR_DEPARTAMENTO: Record<string, ConjuntoColunas> = {
  Faturamento: {
    fixas: ['dias_ativos', 'faturas_calculadas', 'calculadas_por_dia', 'faturas_validadas', 'boletos_emitidos',
      'faturas_puladas', 'pct_corrigidas', 'pct_fora_prazo', 'tempo_emissao'],
    extras: [],
  },
  Financeiro: COBRANCA,
  'Pós Venda': COBRANCA,
  Comercial: COMERCIAL,
  Tecnologia: CENTRAL,
  Marketing: CENTRAL,
};

/** Função sem conjunto próprio: mostra tudo o que a pessoa de fato fez. */
const PADRAO: ConjuntoColunas = {
  fixas: ['dias_ativos'],
  extras: Object.keys(INDICADORES).filter((id) => id !== 'dias_ativos'),
};

/** Ordem das equipes dentro da unidade; as não listadas vêm depois, em ordem alfabética. */
const ORDEM_DEPARTAMENTOS = [
  'Faturamento', 'Financeiro', 'Pós Venda', 'Comercial',
  'Tecnologia', 'Marketing', 'Suporte e Atendimento', 'Motoboy',
];

export const SEM_DEPARTAMENTO = 'Sem departamento';

// ── Cálculo ────────────────────────────────────────────────────────────────

/** Agrupa as linhas da RPC por pessoa. */
export function agruparLinhas(linhas: LinhaIndicador[]): Map<string, Valores> {
  const porPessoa = new Map<string, Valores>();
  for (const l of linhas) {
    const valor = Number(l.valor);
    if (!Number.isFinite(valor)) continue;
    const atual = porPessoa.get(l.user_id) ?? {};
    atual[l.indicador] = valor;
    porPessoa.set(l.user_id, atual);
  }
  return porPessoa;
}

export const normalizarNome = (nome: string | null | undefined) => (nome || '').trim().toLowerCase();

/**
 * Brutos de prazo do Faturamento, por nome de quem emitiu (o histórico de
 * emissão guarda o nome, não o id). Mesmas regras do Dashboard de Faturamento:
 * só a 1ª emissão de cada fatura, sem boleto avulso.
 *
 * Janela meio-aberta [inicio, fimExclusivo); `null` = sem limite daquele lado.
 */
export function valoresDeEmissao(
  emissoes: EmissaoFaturamento[],
  inicio: Date | null,
  fimExclusivo: Date | null,
): Map<string, Valores> {
  const porNome = new Map<string, Valores>();
  for (const e of emissoes) {
    if (!contaComoFaturamento(e)) continue;
    const quando = new Date(e.emitidoEm);
    if (isNaN(quando.getTime())) continue;
    if (inicio && quando < inicio) continue;
    if (fimExclusivo && quando >= fimExclusivo) continue;

    const chave = normalizarNome(e.emitidoPorNome);
    if (!chave) continue;
    const v = porNome.get(chave) ?? { emissoes: 0, emissoes_fora_prazo: 0, emissoes_dias_soma: 0, emissoes_com_tempo: 0 };
    v.emissoes += 1;
    if (emissaoForaDoPrazo(e)) v.emissoes_fora_prazo += 1;
    const dias = tempoDeFaturamentoEmDias(e);
    if (dias !== null) {
      v.emissoes_dias_soma += dias;
      v.emissoes_com_tempo += 1;
    }
    porNome.set(chave, v);
  }
  return porNome;
}

/** Valor de um indicador para UMA pessoa. `null` = não dá para calcular (razão sem denominador). */
export function valorDoIndicador(ind: Indicador, v: Valores): number | null {
  if (ind.calculo.tipo === 'bruto') return v[ind.calculo.chave] ?? 0;
  const den = v[ind.calculo.denominador] ?? 0;
  if (!den) return null;
  return (v[ind.calculo.numerador] ?? 0) / den;
}

/** Valor da linha da EQUIPE: soma, média por pessoa ou soma ÷ soma. */
export function valorDaEquipe(ind: Indicador, membros: Valores[]): number | null {
  if (membros.length === 0) return null;
  const somar = (chave: string) => membros.reduce((acc, v) => acc + (v[chave] ?? 0), 0);
  if (ind.calculo.tipo === 'bruto') {
    const soma = somar(ind.calculo.chave);
    return ind.calculo.equipe === 'media' ? soma / membros.length : soma;
  }
  const den = somar(ind.calculo.denominador);
  if (!den) return null;
  return somar(ind.calculo.numerador) / den;
}

// ── Montagem do painel ─────────────────────────────────────────────────────

export interface LinhaPessoa {
  pessoa: PessoaBi;
  valores: Valores;
  /**
   * Entrou no sistema no período, mas nenhuma coluna da função registrou nada.
   * Quase sempre é trabalho que o sistema ainda não mede (ex.: rateio) — a tela
   * avisa, para a pessoa não parecer parada sem estar.
   */
  semIndicadorMedido: boolean;
}

export interface EquipeBi {
  departamento: string;
  colunas: Indicador[];
  pessoas: LinhaPessoa[];
}

export interface UnidadeBi {
  unidade: Unidade;
  equipes: EquipeBi[];
  totalPessoas: number;
}

const temValor = (ind: Indicador, v: Valores) => {
  const x = valorDoIndicador(ind, v);
  return x !== null && x !== 0;
};

function ordemDepartamento(a: string, b: string): number {
  const ia = ORDEM_DEPARTAMENTOS.indexOf(a);
  const ib = ORDEM_DEPARTAMENTOS.indexOf(b);
  if (a === SEM_DEPARTAMENTO) return 1;
  if (b === SEM_DEPARTAMENTO) return -1;
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return a.localeCompare(b, 'pt-BR');
}

/**
 * Monta o painel: unidade → equipe (departamento do perfil) → pessoas.
 *
 * `emissoesPorNome` entra separado porque chega depois (a carga das emissões é
 * a mais pesada da tela) — sem ele, as colunas de prazo ficam em "—".
 */
export function montarPainelEquipes(
  pessoas: PessoaBi[],
  valoresPorPessoa: Map<string, Valores>,
  emissoesPorNome: Map<string, Valores> = new Map(),
): UnidadeBi[] {
  return UNIDADES.map((unidade) => {
    const daUnidade = pessoas.filter((p) => p.unidade === unidade);

    const porDepartamento = new Map<string, PessoaBi[]>();
    for (const p of daUnidade) {
      // Comercial Interno e Externo viraram uma equipe só, "Comercial" (#6520).
      const dep = nomeDepartamento((p.departamento || '').trim()) || SEM_DEPARTAMENTO;
      porDepartamento.set(dep, [...(porDepartamento.get(dep) ?? []), p]);
    }

    const equipes: EquipeBi[] = Array.from(porDepartamento.entries())
      .sort(([a], [b]) => ordemDepartamento(a, b))
      .map(([departamento, membros]) => {
        const conjunto = COLUNAS_POR_DEPARTAMENTO[departamento] ?? PADRAO;
        const linhas = membros
          .map((pessoa) => ({
            pessoa,
            valores: { ...(valoresPorPessoa.get(pessoa.id) ?? {}), ...(emissoesPorNome.get(normalizarNome(pessoa.nome)) ?? {}) },
          }))
          .sort((a, b) => a.pessoa.nome.localeCompare(b.pessoa.nome, 'pt-BR'));

        const colunas = [
          ...conjunto.fixas,
          ...conjunto.extras.filter((id) => linhas.some((l) => temValor(INDICADORES[id], l.valores))),
        ].map((id) => INDICADORES[id]);

        const daFuncao = colunas.filter((c) => c.id !== 'dias_ativos');
        return {
          departamento,
          colunas,
          pessoas: linhas.map((l) => ({
            ...l,
            semIndicadorMedido: (l.valores.dias_ativos ?? 0) > 0 && !daFuncao.some((c) => temValor(c, l.valores)),
          })),
        };
      });

    return { unidade, equipes, totalPessoas: daUnidade.length };
  });
}

// ── Formatação ─────────────────────────────────────────────────────────────

export function formatarIndicador(valor: number | null, formato: FormatoIndicador): string {
  if (valor === null || !Number.isFinite(valor)) return '—';
  const num = (v: number, casas = 1) => v.toLocaleString('pt-BR', { maximumFractionDigits: casas });
  switch (formato) {
    case 'pct': return `${num(valor * 100)}%`;
    case 'brl': return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    case 'kwh': return `${num(valor, 0)} kWh`;
    case 'dias': return `${num(valor)} ${valor === 1 ? 'dia' : 'dias'}`;
    case 'horas': return `${num(valor)} h`;
    default: return num(valor);
  }
}
